<template>
  <form :action="action" :method="method" @submit="onSubmit" ref="form">
    <input v-if="token" type="hidden" name="_token" :value="token">
    <input type="hidden" name="form_id" :value="formId" v-if="formId">
    <input type="hidden" v-if="addressType !== null" name="type" :value="addressType">
    <fieldset class="uk-fieldset">
      <legend class="uk-legend">
        <slot name="name"></slot>
      </legend>
      <slot name="description"></slot>
      <div class="uk-alert uk-alert-danger" v-if="multipleBillingAddressError">
        <span>{{ $t('rubinred.ecommerce.core.common.addresses.multiple_billing_address_error') }}</span>
      </div>
      <div class="uk-alert uk-alert-danger" v-if="hasErrors">
        <span>{{ $t('rubinred.forms.forms.message.submission.error') }}</span>
      </div>
      <div class="uk-alert uk-alert-success" v-if="hasAlerts">
        <p v-for="message in alerts">{{ message }}</p>
      </div>
      <slot name="fields"></slot>
      <slot name="privacies"></slot>
      <slot name="submit"></slot>
      <slot name="footer"></slot>
    </fieldset>
  </form>
</template>
<script>
  import Form from 'rubinred/forms/js/mixins/Form'

  export default {
    mixins: [Form],
    props: {
      allowOnlyOneBillingAddress: {
        type: Boolean,
        default: false,
      },
      previousBillingAddress: {
        type: Number,
      },
      editAddress: {
        type: [String, Number],
      },
			addressType: {
				type: String,
				default: null,
			},
			billingFieldsSlugs: {
				type: Array,
				default: () => [
					'customer_type',
					'fiscal_code',
					'vat',
					'business_name',
					'electronic_invoice',
					'certified_email',
					'recipient_code',
				],
			}
		},
		data() {
			return {
				type: this.addressType || 'shipping',
				billingFields: [],

				customerTypeField: null,
				fiscalCodeField: null,
				businessNameField: null,
				vatField: null,

				electronicInvoiceField: null,
				certifiedEmailField: null,
				recipientCodeField: null,

        multipleBillingAddressError: false,
			};
		},
		mounted() {
			this.$nextTick(() => this.toggleBillingFields());
		},
		methods: {
      performValidation() {
        this.valid = true;

        if(this.type === 'billing' && this.allowOnlyOneBillingAddress && this.previousBillingAddress && this.previousBillingAddress != this.editAddress) {
          this.valid = false;
          this.multipleBillingAddressError = true;
        } else {
          this.multipleBillingAddressError = false;
        }

        //
        for (let i = 0; i < this.activeFields.length; i++) {
          if (!this.performFieldValidation(this.activeFields[i])) {
            this.valid = false;
          }
        }

        this.activeFields.forEach(field => {
          field.shouldValidate = true;
          if (this.validateOnChange && this.attempts === 0) {
            field.validateOnChange = true;
          }
        });

        window.App.$emit('form.validate', this);
        return this.valid;
      },
      setValues(values, merge = true) { //set to false to set empty values
        let self = this;
        this.multipleBillingAddressError = false;

        this.fields.forEach(field => {
          if(field.slug === 'vat' || field.slug === 'fiscal_code') {
            if(this.type === 'billing' && this.allowOnlyOneBillingAddress && this.previousBillingAddress && this.previousBillingAddress == this.editAddress) {
              field.enabled = false;
            } else {
              field.enabled = !field.disabled
            }
          }

          const value = values[field.slug];
          if (value || !merge) {
            field.setValue(value);
          }
        });
      },
			registerField(field) {
				//We assume that the 'type' field is rendered only when needed
				if (field.slug === 'type') {
					this.type = field.getValue();
					field.$on('change', this.setType);
				}
				if (this.billingFieldsSlugs.includes(field.slug)) {
					switch (field.slug) {
						case 'customer_type':
							this.customerTypeField = field;
							field.$on('change', this.toggleBillingFields);
							break;
						case 'fiscal_code':
							this.fiscalCodeField = field;
							break;
						case 'business_name':
							this.businessNameField = field;
							break;
						case 'vat':
							this.vatField = field;
							break;
						case 'electronic_invoice':
							this.electronicInvoiceField = field;
							break;
						case 'certified_email':
							this.certifiedEmailField = field;
							break;
						case 'recipient_code':
							this.recipientCodeField = field;
							break;
					}
					this.billingFields.push(field);
				}

				Form.methods.registerField.bind(this)(field);
			},
			setType(type) {
				this.type = type;
				this.toggleBillingFields();
				if (type !== 'billing') { //Reset billing fields
					this.billingFields.forEach(field => field.setValue(null));
				}
			},
			toggleBillingFields() {
        let self = this;
				let type = this.type;
				let customerType = this.customerTypeField ? this.customerTypeField.getValue() : null;
				if (type !== 'billing') {
            this.multipleBillingAddressError = false;
					if (this.billingFields.length < 4) {
						return;
					}

					this.billingFields.forEach(field => field.$el.classList.toggle('uk-hidden', true));

					return;
				} else {
          this.$nextTick(() => {
            if(self.allowOnlyOneBillingAddress && self.previousBillingAddress && self.previousBillingAddress != self.editAddress) {
              self.multipleBillingAddressError = true;
            } else {
              self.multipleBillingAddressError = false;
            }
          });
					this.electronicInvoiceField.$el.classList.toggle('uk-hidden', false);
					this.certifiedEmailField.$el.classList.toggle('uk-hidden', false);
					this.recipientCodeField.$el.classList.toggle('uk-hidden', false);
				}

				this.customerTypeField.$el.classList.toggle('uk-hidden', false);
				switch (customerType) {
					case 'vat_owner':
						this.vatField.$el.classList.toggle('uk-hidden', false);
						this.businessNameField.$el.classList.toggle('uk-hidden', false);
						this.fiscalCodeField.$el.classList.toggle('uk-hidden', true);
						break;
					default:
					case 'private':
						this.vatField.$el.classList.toggle('uk-hidden', true);
						this.businessNameField.$el.classList.toggle('uk-hidden', true);
						this.fiscalCodeField.$el.classList.toggle('uk-hidden', false);
						break;
				}
				App.UIkit.update();
			},
			getValues() {
				let values = Form.methods.getValues.bind(this)();
				if (this.addressType !== null) {
					values['type'] = this.addressType;
				}

				return values;
			}
		}
	}
</script>



// WEBPACK FOOTER //
// public/themes/frontend/uikit3/extensions/rubinred/ecommerce/assets/js/components/addressing/AddressForm.vue